/**
 * SENTRY USER MANAGEMENT STRATEGY
 * ------------------------------
 * Because we use AWS Amplify for authentication, Sentry's default identity tracking
 * cannot automatically detect login/logout events.
 * * We manually manage the Sentry User Context to ensure:
 * 1. Post-login errors are associated with a specific user.
 * 2. Pre-login/Public errors remain anonymous.
 * 3. Privacy is maintained by clearing the user context on logout.
 */

import { type App } from 'vue';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { getCurrentUser } from 'aws-amplify/auth';
import { environment } from '@/utils/environment';

interface AmplifyUser {
  userId: string;
  username: string; // email address
  signInDetails: {
    authFlowType: string;
    loginId: string;
  };
}

let amplifyUser: AmplifyUser | null = null;

// Sentry
export const loadSentry = async (app: App, router: Router) => {
  await setSentryUserContext();

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment,
    release: import.meta.env.VITE_VERSION,
    integrations: [
      Sentry.vueIntegration({
        tracingOptions: {
          // Automatically track component lifecycle for better error context
          trackComponents: true
        }
      }),
      Sentry.browserTracingIntegration({
        router
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      })
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5
  });
};

/**
 * Sets the Sentry user context.
 * Call this:
 * 1. During app initialization (loadSentry) to catch returning sessions.
 * 2. Immediately after successful login/signup in your auth composable.
 */
export const setSentryUserContext = async () => {
  try {
    amplifyUser = (await getCurrentUser()) as unknown as AmplifyUser;

    if (amplifyUser) {
      Sentry.setUser({
        email: amplifyUser.username,
        id: amplifyUser.userId,
        username: amplifyUser.signInDetails.loginId
      });

      // Set tags for filtering/searching
      // This helps identify if 2FA or specific auth flows are crashing the app
      if (amplifyUser.signInDetails?.authFlowType) {
        Sentry.setTag('auth.flow_type', amplifyUser.signInDetails.authFlowType);
      }

      return;
    }

    // Clear context if no user
    Sentry.setUser(null);
  } catch (error) {
    /*
     * No user is currently authenticated. This is expected for users who have not logged in yet.
     * Sentry will remain anonymous until the user logs in and this function is called again to set the user context.
     */
    console.warn(
      'No authenticated user found for Sentry context. User will be anonymous until login.',
      error
    );

    // Clear context on auth error
    Sentry.setUser(null);

    Sentry.setTag('auth.flow_type', null);
  }
};

/**
 * Explicitly clears the Sentry user.
 * Necessary in SPAs to prevent the "Ghost User" bug, where errors from a new
 * anonymous session are incorrectly attributed to the previous authenticated user.
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag('auth_flow', null);
};

/**
 * Standardized error logger for better Sentry visibility.
 *
 * @param error - The error object
 * @param function - The name of the function where the error occurred
 * @param section - The section where the error occurred
 * @param level - The severity level (optional; Sentry uses defaults if omitted)
 * @param message - A custom message to provide additional context about the error
 * @param metadata - Any extra variables (like 'attempt' or 'userId')
 */
export const logAppError = ({
  error,
  functionName,
  section,
  level,
  message,
  metadata = {}
}: {
  error: unknown;
  functionName: string;
  section?: string;
  level?: 'fatal' | 'warning' | 'info' | 'debug';
  message?: string;
  metadata?: Record<string, any>;
}) => {
  Sentry.withScope((scope) => {
    // Always set the function tag
    scope.setTag('function', functionName);

    // Only set the section tag if it's provided
    if (section) {
      scope.setTag('section', section);
    }

    // Only override level if explicitly provided
    if (level) {
      scope.setLevel(level);
    }

    if (message) {
      metadata.contextMessage = message;
    }

    // Set the fingerprint for better grouping
    // This uses the function and section to distinguish between generic errors
    const fingerprint = ['{{ default }}', functionName];
    if (section) fingerprint.push(section);
    scope.setFingerprint(fingerprint);

    // Attach extra metadata
    if (metadata) {
      scope.setExtras(metadata);
    }

    Sentry.captureException(error);
  });
};
